/* eslint-disable max-lines */
import {
  ConversationEscalationResponse,
  ConversationResponse
} from "@9amhealth/openapi";
import { AiChatError, AiChatStream, type ToolCall } from "@9amhealth/shared";
import { Cubit } from "blac-next";
import envVariables from "src/lib/envVariables";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import createTrackEvent from "src/lib/createTrackEvent";
import { authenticationState, toast, tracker } from "src/state/state";
import { ConversationService } from "./ConversationService";
import {
  ChatEventType,
  type ChatPrompt,
  type ChatPromptOption,
  type DebugOverrides,
  type LeapChatAttachment,
  type LeapChatFileRef,
  type LeapChatMessage,
  type MessageSendError,
  llmModels,
  normalizeMessageError
} from "./types";
import { ChatErrorSimulator } from "./ChatErrorSimulator";
import { normalizeMessageText } from "./utils";

export interface ConversationThreadState {
  messages: LeapChatMessage[];
  isRunning: boolean;
  isInitializing: boolean;
  inputValue: string;
  attachments: LeapChatAttachment[];
  activeUserMessageId: string | null;
  stickyBottom: boolean;
  prompts: Record<string, ChatPrompt>;
  escalated: boolean;
  escalationBannerIndex: number | null;
  isOffline: boolean;
  overrides?: DebugOverrides;
}

const SCROLL_STICK_THRESHOLD = 10;

export default class ConversationThreadCubit extends Cubit<ConversationThreadState> {
  private service = ConversationService.getInstance();
  private abortController: AbortController | null = null;
  private unsubConversations: (() => void) | null = null;
  private unsubMessages: (() => void) | null = null;
  protected StreamClass: typeof AiChatStream = AiChatStream;

  autoScrollInProgress = false;
  domElements: {
    scrollEl: HTMLDivElement | null;
    contentEl: HTMLDivElement | null;
    composerEl: HTMLDivElement | null;
  } = { scrollEl: null, contentEl: null, composerEl: null };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private promptRegistry: Record<
    string,
    | {
        options: ChatPromptOption[];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSelect: (value: string, eventData: any) => void;
      }
    | undefined
  > = {
    [ChatEventType.EscalationPrompt]: {
      options: [
        {
          label: translate("leapChat.escalation.confirm"),
          value: "confirm",
          variant: "primary"
        },
        {
          label: translate("leapChat.escalation.cancel"),
          value: "cancel",
          variant: "secondary"
        }
      ],
      onSelect: (
        ...args: Parameters<typeof this.handleSelectEscalationPromptOption>
      ) => this.handleSelectEscalationPromptOption(...args)
    }
  };

  conversationId: string | undefined;

  constructor() {
    super({
      messages: [],
      isRunning: false,
      isInitializing: false,
      inputValue: "",
      attachments: [],
      activeUserMessageId: null,
      stickyBottom: false,
      prompts: {},
      escalated: false,
      escalationBannerIndex: null,
      isOffline: typeof navigator !== "undefined" ? !navigator.onLine : false
    });
  }

  init = (conversationId: string): void => {
    this.conversationId = conversationId;
    this.service.setActiveConversation(conversationId);
    this.unsubConversations = this.service.onConversationsChanged(() => {
      this.updateEscalationState();
    });

    this.unsubMessages = this.service.onNewMessage((convId, message) => {
      if (convId === this.conversationId) {
        this.handleIncomingWsMessage(message);
      }
    });

    window.addEventListener("online", this.handleOnline);
    window.addEventListener("offline", this.handleOffline);

    void this.load();
  };

  dispose(): void {
    this.cancelStream();
    this.service.setActiveConversation(null);
    this.service.revokePreviewUrls(this.state.attachments);
    this.unsubConversations?.();
    this.unsubMessages?.();
    this.cleanupDomListeners();
    window.removeEventListener("online", this.handleOnline);
    window.removeEventListener("offline", this.handleOffline);
  }

  private handleOnline = (): void => {
    this.patch({ isOffline: false });
  };

  private handleOffline = (): void => {
    this.patch({ isOffline: true });
  };

  // -- Load --

  private async load(): Promise<void> {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }

    const [cached, cachedPrompts] = await Promise.all([
      this.service.getCachedMessages(conversationId),
      this.service.getCachedPrompts(conversationId)
    ]);

    const prompts: Record<string, ChatPrompt> = {};
    for (const p of cachedPrompts) {
      prompts[p.messageId] = p;
    }

    const normalized = cached.map((m) => {
      const err = normalizeMessageError(
        m.error as string | MessageSendError | undefined
      );
      return err !== m.error ? { ...m, error: err } : m;
    });

    if (normalized.length > 0) {
      this.patch({
        messages: this.sortMessages(normalized),
        prompts,
        isInitializing: false
      });
      this.markLastMessageReadIfNeeded(normalized);
      this.scrollToBottomAfterRender();
    } else if (cachedPrompts.length > 0) {
      this.patch({ prompts });
    }

    if (this.service.isMessageSyncFresh(conversationId)) {
      this.patch({ isInitializing: false });
      this.updateEscalationState();
      this.scrollToBottomAfterRender();
    } else {
      try {
        const remoteMessages = await this.service.fetchMessages(conversationId);
        if (this.conversationId !== conversationId) {
          return;
        }

        const remoteIds = new Set(remoteMessages.map((m) => m.id));
        const failedMessages = (
          normalized.length > 0 ? normalized : this.state.messages
        ).filter((m) => m.error && !remoteIds.has(m.id));

        this.patchMessagesIfChanged([...remoteMessages, ...failedMessages], {
          isInitializing: false
        });
        this.updateEscalationState();
        this.scrollToBottomAfterRender();

        void this.service.syncMessagesCache(conversationId, remoteMessages);
      } catch (e) {
        reportErrorSentry(e);
        this.patch({ isInitializing: false });
      }
    }

    const pending = this.service.consumePendingFirstMessage();
    if (pending) {
      void this.sendMessage(pending.text, pending.fileIds, pending.files);
    }
  }

  // -- Incoming messages (WebSocket) --

  private handleIncomingWsMessage(message: LeapChatMessage): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }

    const alreadyExists = this.state.messages.some((m) => m.id === message.id);
    if (alreadyExists) {
      return;
    }

    const shouldMarkUnread = !this.state.stickyBottom;
    const incoming = shouldMarkUnread ? { ...message, unread: true } : message;
    const updated = [...this.state.messages, incoming];

    this.patchMessagesIfChanged(updated);

    if (!shouldMarkUnread && message.role !== "user") {
      void this.service.markRead(conversationId, message.id);
    }
  }

  // -- Helpers --

  private get conversation(): ConversationResponse | null {
    const { conversationId } = this;
    if (!conversationId) {
      return null;
    }
    return this.service.getConversation(conversationId);
  }

  private sortMessages(messages: LeapChatMessage[]): LeapChatMessage[] {
    return messages.slice().sort((a, b) => a.createdAt - b.createdAt);
  }

  private messagesEqual(a: LeapChatMessage[], b: LeapChatMessage[]): boolean {
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      const am = a[i];
      const bm = b[i];
      if (
        am.id !== bm.id ||
        am.text !== bm.text ||
        am.createdAt !== bm.createdAt ||
        am.isLocal !== bm.isLocal ||
        am.unread !== bm.unread ||
        am.error?.code !== bm.error?.code ||
        am.error?.statusCode !== bm.error?.statusCode
      ) {
        return false;
      }
    }
    return true;
  }

  private patchMessagesIfChanged(
    messages: LeapChatMessage[],
    extra?: Partial<ConversationThreadState>
  ): boolean {
    const sorted = this.sortMessages(messages);
    if (this.messagesEqual(this.state.messages, sorted) && !extra) {
      return false;
    }
    if (this.messagesEqual(this.state.messages, sorted) && extra) {
      this.patch(extra);
      return false;
    }
    this.patch({ messages: sorted, ...extra });
    this.markLastMessageReadIfNeeded(sorted);
    return true;
  }

  private persistCurrentMessages(): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }
    const messagesToPersist = this.state.messages.filter(
      (m) => !(m.error && m.role === "assistant" && m.localId === m.id)
    );
    void this.service.persistMessages(conversationId, messagesToPersist);
  }

  private markLastMessageReadIfNeeded(messages: LeapChatMessage[]): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }
    const last = messages[messages.length - 1];
    if (last && last.role !== "user") {
      void this.service.markRead(conversationId, last.id);
    }
  }

  // -- DOM / scroll --

  setDomElements = (elements: {
    scrollEl: HTMLDivElement | null;
    contentEl: HTMLDivElement | null;
    composerEl: HTMLDivElement | null;
  }): void => {
    const prevScroll = this.domElements.scrollEl;
    const { scrollEl } = elements;

    if (prevScroll && prevScroll !== scrollEl) {
      prevScroll.removeEventListener("wheel", this.handleManualScroll);
      prevScroll.removeEventListener("touchstart", this.handleManualScroll);
      prevScroll.removeEventListener("scroll", this.handleScrollEvent);
    }

    if (scrollEl && scrollEl !== prevScroll) {
      scrollEl.addEventListener("wheel", this.handleManualScroll);
      scrollEl.addEventListener("touchstart", this.handleManualScroll);
      scrollEl.addEventListener("scroll", this.handleScrollEvent);
    }

    this.domElements = { ...elements };
  };

  private cleanupDomListeners(): void {
    const el = this.domElements.scrollEl;
    if (el) {
      el.removeEventListener("wheel", this.handleManualScroll);
      el.removeEventListener("touchstart", this.handleManualScroll);
      el.removeEventListener("scroll", this.handleScrollEvent);
    }
  }

  scrollToBottom = (): void => {
    const el = this.domElements.scrollEl;
    if (!el) {
      return;
    }
    this.autoScrollInProgress = true;
    el.scrollTo({ top: el.scrollHeight + 500, behavior: "instant" });
    requestAnimationFrame(() => {
      this.autoScrollInProgress = false;
    });
  };

  private scrollToBottomAfterRender(): void {
    requestAnimationFrame(() => {
      this.scrollToBottom();
    });
  }

  restoreScrollIfNeeded = (): void => {
    if (this.state.stickyBottom) {
      this.scrollToBottom();
    }
  };

  private handleManualScroll = (): void => {
    if (this.state.stickyBottom) {
      this.patch({ stickyBottom: false });
    }
  };

  private handleScrollEvent = (): void => {
    const el = this.domElements.scrollEl;
    if (!el || this.autoScrollInProgress) {
      return;
    }

    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const shouldStick = distanceFromBottom < SCROLL_STICK_THRESHOLD;

    if (shouldStick !== this.state.stickyBottom) {
      this.patch({ stickyBottom: shouldStick });
    }

    if (shouldStick) {
      this.markMessagesAsRead();
    }
  };

  markMessagesAsRead = (): void => {
    const hasUnread = this.state.messages.some((m) => m.unread);
    if (!hasUnread) {
      return;
    }

    const { conversationId } = this;
    if (!conversationId) {
      return;
    }

    const updated = this.state.messages.map((m) =>
      m.unread ? { ...m, unread: undefined } : m
    );
    this.patch({ messages: updated });
    this.persistCurrentMessages();
  };

  updateNewMessagePositioning = (): void => {
    const { scrollEl, contentEl, composerEl } = this.domElements;
    if (!scrollEl || !contentEl) {
      return;
    }

    const lastUserEl =
      contentEl.querySelector<HTMLElement>('[data-last="user"]');
    const composerHeight = composerEl?.clientHeight ?? 0;
    const userMsgHeight = lastUserEl?.offsetHeight ?? 0;
    const setHeight = scrollEl.clientHeight - composerHeight - userMsgHeight;

    if (setHeight > 0) {
      contentEl.style.setProperty(
        "--assistant-response-placeholder-height",
        `${setHeight.toFixed(1)}px`
      );
    }

    lastUserEl?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };

  // -- Input --

  setInputValue = (value: string): void => {
    this.patch({ inputValue: value });
  };

  // -- Attachments --

  addAttachment = (file: File): void => {
    const attachment = this.service.createAttachment(file);
    this.patch({ attachments: [...this.state.attachments, attachment] });

    void this.service
      .uploadAttachmentFile(file)
      .then((fileId) => {
        if (fileId) {
          this.patchAttachment(attachment.key, { fileId, status: "done" });
        } else {
          this.patchAttachment(attachment.key, { status: "error" });
        }
      })
      .catch((err) => {
        reportErrorSentry(err);
        this.patchAttachment(attachment.key, { status: "error" });
      });
  };

  private patchAttachment(
    key: string,
    partial: Partial<LeapChatAttachment>
  ): void {
    this.patch({
      attachments: this.state.attachments.map((a) =>
        a.key === key ? { ...a, ...partial } : a
      )
    });
  }

  removeAttachment = (key: string): void => {
    const target = this.state.attachments.find((a) => a.key === key);
    if (target?.previewUrl) {
      URL.revokeObjectURL(target.previewUrl);
    }
    this.patch({
      attachments: this.state.attachments.filter((a) => a.key !== key)
    });
  };

  clearAttachments = (): void => {
    this.service.revokePreviewUrls(this.state.attachments);
    this.patch({ attachments: [] });
  };

  // -- Submit --

  private canSubmit(): boolean {
    const { attachments, isRunning, inputValue } = this.state;
    const doneCount = attachments.filter((a) => a.status === "done").length;
    const hasContent = inputValue.trim().length > 0 || doneCount > 0;
    const hasUploading = attachments.some((a) => a.status === "uploading");
    const hasError = attachments.some((a) => a.status === "error");
    return hasContent && !hasUploading && !hasError && !isRunning;
  }

  submitInput = (): void => {
    if (!this.canSubmit()) {
      return;
    }

    const text = this.state.inputValue.trim();
    const { fileIds, files } = this.service.extractAttachmentPayload(
      this.state.attachments
    );

    this.patch({ inputValue: "" });
    this.clearAttachments();
    void this.sendMessage(text || "", fileIds, files);
  };

  // -- Messaging --

  sendMessage = async (
    text: string,
    fileIds?: string[],
    files?: LeapChatFileRef[]
  ): Promise<void> => {
    const normalized = normalizeMessageText(text);
    if ((!normalized && !fileIds?.length) || this.state.isRunning) {
      return;
    }

    this.patch({ isRunning: true });
    const userMsgId = this.addNewUserMessage(normalized, files);
    const assistantMsgId = crypto.randomUUID();
    this.persistCurrentMessages();

    if (!navigator.onLine) {
      this.markMessageError(
        userMsgId,
        assistantMsgId,
        new AiChatError("offline", "Device is offline")
      );
      this.patch({ isRunning: false });
      return;
    }

    try {
      await this.streamResponse(assistantMsgId, normalized, fileIds, userMsgId);
    } catch (e) {
      this.handleStreamError(e, userMsgId, assistantMsgId);
    } finally {
      this.abortController = null;
      this.patch({ isRunning: false });
    }
  };

  private addNewUserMessage(text: string, files?: LeapChatFileRef[]): string {
    const localId = crypto.randomUUID();
    this.patch({
      messages: [
        ...this.state.messages,
        {
          id: localId,
          localId,
          role: "user",
          text,
          files,
          createdAt: Date.now(),
          isLocal: true
        }
      ],
      activeUserMessageId: localId
    });
    return localId;
  }

  private addNewAssistantMessage(id?: string): string {
    const localId = id ?? crypto.randomUUID();
    this.patch({
      messages: [
        ...this.state.messages,
        {
          id: localId,
          localId,
          role: "assistant",
          text: "",
          createdAt: Date.now(),
          isGeneratedResponse: true
        }
      ]
    });
    return localId;
  }

  retryMessage = (messageId: string): void => {
    const msg = this.state.messages.find((m) => m.id === messageId);
    if (!msg?.error || this.state.isRunning) {
      return;
    }

    const fileIds = msg.files?.map((f) => f.fileId);
    const assistantMsgId = crypto.randomUUID();

    const retryMsg = {
      ...msg,
      error: undefined,
      isLocal: true,
      createdAt: Date.now()
    };
    const remaining = this.state.messages.filter((m) => m.id !== messageId);
    this.patch({
      isRunning: true,
      messages: [...remaining, retryMsg],
      activeUserMessageId: messageId
    });

    this.persistCurrentMessages();

    if (!navigator.onLine) {
      this.markMessageError(
        messageId,
        assistantMsgId,
        new AiChatError("offline", "Device is offline")
      );
      this.patch({ isRunning: false });
      return;
    }

    void this.streamResponse(assistantMsgId, msg.text, fileIds, messageId)
      .catch((e) => {
        this.handleStreamError(e, messageId, assistantMsgId);
      })
      .finally(() => {
        this.abortController = null;
        this.patch({ isRunning: false });
      });
  };

  deleteMessage = (messageId: string): void => {
    const msg = this.state.messages.find((m) => m.id === messageId);
    if (!msg?.error) {
      return;
    }

    this.patch({
      messages: this.state.messages.filter((m) => m.id !== messageId)
    });
    void this.service.deleteMessage(messageId);
  };

  cancelStream = (): void => {
    this.abortController?.abort();
  };

  // -- Streaming --

  private async streamResponse(
    assistantMsgId: string,
    text: string,
    fileIds?: string[],
    userMsgId?: string
  ): Promise<void> {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }
    this.abortController = new AbortController();

    const simulatedError = ChatErrorSimulator.consume();
    if (simulatedError) {
      throw simulatedError;
    }

    const body: Record<string, unknown> = {
      contentValue: text,
      contentType: "text/plain",
      conversationId
    };
    if (fileIds?.length) {
      body.fileIds = fileIds;
    }

    if (!this.state.escalated) {
      this.addNewAssistantMessage(assistantMsgId);
    }

    let currentAssistantMsgId = assistantMsgId;
    let currentUserMsgId = userMsgId;
    let deliveredMarked = false;

    const stream = new this.StreamClass({
      url: `${envVariables.API_BASE_URL}/v1/ai/ama`,
      body,
      headers: {
        Authorization: `Bearer ${authenticationState.accessToken}`
      },
      signal: this.abortController.signal,
      overrides: this.state.overrides,
      onEventType: {
        [ChatEventType.EscalationPrompt]: (data) => {
          this.showPrompt(
            ChatEventType.EscalationPrompt,
            data,
            currentAssistantMsgId,
            conversationId
          );
        },
        "messaging.reply.start": (data) => {
          const { referenceMessageId, senderDisplayName, senderDisplayRole } =
            data as {
              referenceMessageId: string;
              senderDisplayName: string;
              senderDisplayRole: string;
            };
          if (currentUserMsgId) {
            this.updateMessageId(currentUserMsgId, referenceMessageId);
            currentUserMsgId = referenceMessageId;
            if (!deliveredMarked) {
              deliveredMarked = true;
              this.markUserMessageDelivered(referenceMessageId);
            }
          }
          this.patch({
            messages: this.state.messages.map((msg) =>
              msg.id === currentAssistantMsgId
                ? {
                    ...msg,
                    senderName: senderDisplayName,
                    senderDescription: senderDisplayRole
                  }
                : msg
            )
          });
        },
        "messaging.reply.end": (data) => {
          const { replyMessageId } = data as { replyMessageId: string };
          this.updateMessageId(currentAssistantMsgId, replyMessageId);
          currentAssistantMsgId = replyMessageId;
        },
        "messaging.reply.error": () => {
          this.handleReplyError(currentAssistantMsgId);
        }
      },
      onChunk: (fullText, toolCalls) => {
        this.patchAssistantMessage(currentAssistantMsgId, fullText, toolCalls);
      },
      onComplete: (fullText, toolCalls) => {
        this.patchAssistantMessage(
          currentAssistantMsgId,
          fullText,
          toolCalls.length > 0 ? toolCalls : undefined
        );
        if (!deliveredMarked && currentUserMsgId) {
          deliveredMarked = true;
          this.markUserMessageDelivered(currentUserMsgId);
        }
        this.persistCurrentMessages();
      }
    });

    await stream.start();
  }

  private updateMessageId(oldId: string, newId: string): void {
    if (oldId === newId) {
      return;
    }

    const currentMsg = this.state.messages.find((m) => m.id === oldId);
    if (!currentMsg) {
      return;
    }

    const newMsg: LeapChatMessage = { ...currentMsg, id: newId };
    const newMessageList = this.state.messages.map((msg) =>
      msg.id === oldId ? newMsg : msg
    );

    const patch: Partial<ConversationThreadState> = {
      messages: newMessageList
    };

    const oldPrompt = this.state.prompts[oldId];
    if (oldPrompt) {
      const updatedPrompt = { ...oldPrompt, messageId: newId };
      const { [oldId]: _, ...rest } = this.state.prompts;
      patch.prompts = { ...rest, [newId]: updatedPrompt };
      void this.service.putPrompt(updatedPrompt);
    }

    this.patch(patch);
  }

  private patchAssistantMessage(
    messageId: string,
    text: string,
    toolCalls?: ToolCall[]
  ): void {
    this.patch({
      messages: this.state.messages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              text,
              toolCalls: toolCalls?.length ? [...toolCalls] : undefined
            }
          : msg
      )
    });
  }

  private handleStreamError(
    e: unknown,
    userMsgId: string | undefined,
    assistantMsgId: string
  ): void {
    if (e instanceof DOMException && e.name === "AbortError") {
      return;
    }
    reportErrorSentry(e);
    const chatError =
      e instanceof AiChatError
        ? e
        : new AiChatError(
            "unknown",
            e instanceof Error ? e.message : String(e)
          );
    this.markMessageError(userMsgId, assistantMsgId, chatError);
  }

  private handleReplyError(assistantMsgId: string): void {
    const error: MessageSendError = { code: "reply_error" };
    this.patch({
      messages: this.state.messages.map((m) =>
        m.id === assistantMsgId ? { ...m, error } : m
      )
    });
    this.persistCurrentMessages();
  }

  private markMessageError(
    userMsgId: string | undefined,
    assistantMsgId: string,
    chatError: AiChatError
  ): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }

    if (chatError.code === "auth") {
      void authenticationState.refreshAccessToken().catch(reportErrorSentry);
    }

    const sendError: MessageSendError = {
      code: chatError.code,
      statusCode: chatError.statusCode
    };
    const updated = this.state.messages
      .filter((msg) => msg.id !== assistantMsgId)
      .map((msg) =>
        msg.id === userMsgId ? { ...msg, error: sendError } : msg
      );
    this.patch({ messages: updated });
    this.persistCurrentMessages();
  }

  private markUserMessageDelivered(messageId: string): void {
    const { conversationId } = this;
    if (!conversationId) {
      return;
    }
    this.patch({
      messages: this.state.messages.map((m) =>
        m.id === messageId ? { ...m, isLocal: false } : m
      )
    });
    this.persistCurrentMessages();
  }

  // -- Prompts --

  showPrompt = (
    eventType: string,
    eventData: unknown,
    messageId: string,
    conversationId: string
  ): void => {
    const entry = this.promptRegistry[eventType];
    if (!entry) {
      return;
    }
    const prompt: ChatPrompt = {
      id: crypto.randomUUID(),
      messageId,
      conversationId,
      eventType,
      eventData,
      options: entry.options,
      selectedValue: null
    };
    this.patch({
      prompts: { ...this.state.prompts, [messageId]: prompt }
    });
    void this.service.putPrompt(prompt);
  };

  selectPromptOption = (messageId: string, value: string): void => {
    const prompt = this.state.prompts[messageId];
    if (!prompt) {
      return;
    }

    const entry = this.promptRegistry[prompt.eventType];
    entry?.onSelect(value, prompt.eventData);

    const updatedPrompts = { ...this.state.prompts };
    for (const [key, p] of Object.entries(updatedPrompts)) {
      if (p.eventType === prompt.eventType && p.selectedValue === null) {
        updatedPrompts[key] = { ...p, selectedValue: value };
      }
    }
    this.patch({ prompts: updatedPrompts });
    void this.service.resolvePromptsByType(
      prompt.conversationId,
      prompt.eventType,
      value
    );
  };

  private handleSelectEscalationPromptOption = (
    value: string,
    eventData: { conversationId: string }
  ): void => {
    if (value !== "confirm") {
      tracker.track(createTrackEvent("Escalation Cancelled"), {
        data: { "Conversation ID": eventData.conversationId }
      });
      return;
    }

    this.service
      .createEscalation(eventData.conversationId)
      .then((res) => {
        toast.success("leapChat.escalation.success");
        const nowIso = new Date().toISOString();
        this.service.updateConversation(eventData.conversationId, (c) => ({
          ...c,
          escalations: [
            ...c.escalations,
            {
              id: res.escalationId,
              status: ConversationEscalationResponse.status.OPEN,
              createdAt: nowIso,
              updatedAt: nowIso
            }
          ]
        }));
        this.updateEscalationState();
        void this.service.listConversations();
      })
      .catch((e) => {
        toast.error("leapChat.escalation.error");
        reportErrorSentry(e);
      });
  };

  // -- Escalation state --

  private updateEscalationState(): void {
    const conv = this.conversation;
    if (!conv) {
      this.patch({ escalated: false, escalationBannerIndex: null });
      return;
    }

    const escalated = ConversationService.isEscalated(conv);

    const escalation = conv.escalations.find(
      (e) => e.status === ConversationEscalationResponse.status.OPEN
    );
    const ts = escalation ? new Date(escalation.createdAt).getTime() : null;

    let escalationBannerIndex: number | null = null;
    if (ts !== null) {
      const messages = this.state.messages;
      const idx = messages.findIndex((m) => m.createdAt > ts);

      if (idx === -1) {
        escalationBannerIndex = messages.length;
      } else if (idx > 0) {
        escalationBannerIndex = idx;
      }
    }

    this.patch({ escalated, escalationBannerIndex });
  }

  // -- Debug --

  updateDebugOptions = (options: {
    model?: (typeof llmModels)[number] | undefined;
    temperature?: number | undefined;
    reasoningEffort?: DebugOverrides["reasoningEffort"];
    systemPrompt?: string | undefined;
  }): void => {
    this.patch({
      overrides: {
        ...this.state.overrides,
        ...("model" in options && {
          llmModel: options.model
            ? {
                llmService: options.model.llmService,
                modelName: options.model.modelName
              }
            : undefined
        }),
        ...("temperature" in options && {
          temperature: options.temperature
        }),
        ...("reasoningEffort" in options && {
          reasoningEffort: options.reasoningEffort
        }),
        ...("systemPrompt" in options && {
          systemPrompt: options.systemPrompt
        })
      }
    });
  };
}
